import React, { useRef, useState, useEffect } from "react";
import useInViewVideo from "@arctheme-hooks/useInViewVideo";
import useVideoSource from "@arctheme-hooks/useVideoSourceQuality";

const TileVideo = ({ data, height, isMobile }) => {
    const [aspectRatio, setAspectRatio] = useState('16/9'); 
    const [videoLoaded, setVideoLoaded] = useState(false);
    const { videoRef, isPlaying, setupObserver } = useInViewVideo();
    const videoElementRef = useRef(null);
    
    const videoSettings = isMobile && data.settings?.video_mobile
        ? data.settings?.video_mobile
        : data.settings?.video;

    const { videoUrl, videoPoster } = useVideoSource(
        videoSettings?.sources,
        videoSettings?.preview_image,
        isMobile,
        data.globals?.video_quality
    );
    
    const preview_image = isMobile ? data.settings?.preview_image_mobile  : data.settings?.preview_image;

    useEffect(() => {
        if (videoElementRef.current && videoUrl) {
            const preloadImage = new Image();
            if (preview_image || videoPoster) {
                preloadImage.onload = () => {
                    if (preloadImage.width && preloadImage.height) {
                        setAspectRatio(`${preloadImage.width} / ${preloadImage.height}`);
                    }
                };
                preloadImage.src = preview_image || videoPoster;
            }
            const handleLoadedMetadata = () => {
                const videoWidth = videoElementRef.current.videoWidth;
                const videoHeight = videoElementRef.current.videoHeight;
                if (videoWidth && videoHeight) {
                    setAspectRatio(`${videoWidth} / ${videoHeight}`);
                    setVideoLoaded(true);
                }
            };
            
            videoElementRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
            
            return () => {
                if (videoElementRef.current) {
                    videoElementRef.current.removeEventListener('loadedmetadata', handleLoadedMetadata);
                }
            };
        }
    }, [videoUrl, preview_image, videoPoster]);
    
    const containerStyle = {
        aspectRatio: aspectRatio,
        height: height || 'auto',
        width: '100%',
        position: 'relative' as const,
        overflow: 'hidden'
    };
    
    const videoStyle = {
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const,
        opacity: videoLoaded ? 1 : 0,
        transition: 'opacity 0.3s ease'
    };

    return (
        <div className="h-full w-full" style={containerStyle}>
            <video
                ref={(el) => {
                    videoElementRef.current = el;
                    if (data.settings?.autoplay && videoRef) {
                        videoRef.current = el;
                    }
                }}
                muted={true}
                controls={!data.settings?.autoplay}
                loop={data.settings?.loop || false}
                autoPlay={data.settings?.autoplay || false}
                style={videoStyle}
                preload="metadata"
                poster={preview_image || videoPoster}
                playsInline
            >
                <source src={videoUrl} type="video/mp4" />
            </video>
        </div>
    );
};

export default TileVideo;